/**
 * IndexedDB persistence layer for call recordings.
 *
 * Stores audio chunks during a call so recordings survive page refreshes,
 * tab crashes, and network failures. On reconnect, orphaned recordings
 * are automatically retried.
 *
 * All functions handle IndexedDB being unavailable (e.g. private browsing
 * in some browsers) gracefully — they return null/empty and log a warning.
 */

import { openDB as idbOpen, type IDBPDatabase } from 'idb'
import { api } from '@/api/client'
import { toast } from '@/stores/toast'

const DB_NAME = 'claro-call-recordings'
const DB_VERSION = 1

type RecordingStatus = 'recording' | 'pending_upload' | 'uploading'

interface RecordingRecord {
  id?: number
  callId: number | null
  meetingCode: string | null
  patientUuid: string | null
  callNoteId: number | null
  callNoteUuid: string | null
  callNotes: string
  appointmentId: number | null
  status: RecordingStatus
  // Stable idempotency key minted when the recording is created and persisted
  // here, so the initial upload AND every orphan-recovery retry send the SAME
  // key. Without it, a crash/lost-response after the backend committed but
  // before deleteRecording ran made recovery mint a fresh random key → a
  // duplicate clinical note from one consultation. Optional so records written
  // before this field shipped still load (recovery falls back to a fresh key).
  idempotencyKey?: string
  createdAt: string
  updatedAt: string
}

interface ChunkRecord {
  id?: number
  recordingId: number
  batchIndex: number
  data: Blob
}

type CallRecordingDB = {
  recordings: {
    key: number
    value: RecordingRecord
  }
  chunks: {
    key: number
    value: ChunkRecord
    indexes: { recordingId: number }
  }
}

let dbPromise: Promise<IDBPDatabase<CallRecordingDB>> | null = null

function getDB(): Promise<IDBPDatabase<CallRecordingDB>> | null {
  if (typeof indexedDB === 'undefined') {
    console.warn('[call-recording-db] IndexedDB not available')
    return null
  }
  if (!dbPromise) {
    dbPromise = idbOpen<CallRecordingDB>(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains('recordings')) {
          db.createObjectStore('recordings', { keyPath: 'id', autoIncrement: true })
        }
        if (!db.objectStoreNames.contains('chunks')) {
          const chunks = db.createObjectStore('chunks', { keyPath: 'id', autoIncrement: true })
          chunks.createIndex('recordingId', 'recordingId')
        }
      },
    }).catch((e) => {
      console.warn('[call-recording-db] Failed to open database:', e)
      dbPromise = null
      throw e
    })
  }
  return dbPromise
}

/**
 * Create a new recording entry, minting a durable idempotency key for it.
 * Returns the auto-generated ID and the key, or null on failure. The key is
 * persisted on the record so the initial upload and any later orphan-recovery
 * retry reuse it — the backend dedupes them to a single note.
 */
export async function createRecording(metadata: {
  callId: number | null
  meetingCode: string | null
  patientUuid: string | null
  callNoteId: number | null
  callNoteUuid: string | null
  callNotes: string
  appointmentId: number | null
}): Promise<{ id: number; idempotencyKey: string } | null> {
  try {
    const promise = getDB()
    if (!promise) return null
    const db = await promise
    const now = new Date().toISOString()
    const idempotencyKey = crypto.randomUUID()
    const id = await db.add('recordings', {
      ...metadata,
      idempotencyKey,
      status: 'recording',
      createdAt: now,
      updatedAt: now,
    })
    return { id, idempotencyKey }
  } catch (e) {
    console.warn('[call-recording-db] createRecording failed:', e)
    return null
  }
}

/** Append a batch of audio data as a single blob chunk. */
export async function appendChunks(
  recordingId: number,
  batchIndex: number,
  blob: Blob,
): Promise<void> {
  try {
    const promise = getDB()
    if (!promise) return
    const db = await promise
    await db.add('chunks', { recordingId, batchIndex, data: blob })
  } catch (e) {
    console.warn('[call-recording-db] appendChunks failed:', e)
  }
}

/** Update the status of a recording. */
export async function updateRecordingStatus(
  recordingId: number,
  status: RecordingStatus,
): Promise<void> {
  try {
    const promise = getDB()
    if (!promise) return
    const db = await promise
    const record = await db.get('recordings', recordingId)
    if (record) {
      record.status = status
      record.updatedAt = new Date().toISOString()
      await db.put('recordings', record)
    }
  } catch (e) {
    console.warn('[call-recording-db] updateRecordingStatus failed:', e)
  }
}

/** Find recordings that were left in 'pending_upload' state (e.g. after a crash). */
export async function getOrphanedRecordings(): Promise<RecordingRecord[]> {
  try {
    const promise = getDB()
    if (!promise) return []
    const db = await promise
    const all = await db.getAll('recordings')
    return all.filter((r) => r.status === 'pending_upload')
  } catch (e) {
    console.warn('[call-recording-db] getOrphanedRecordings failed:', e)
    return []
  }
}

/** Reassemble all chunks for a recording into a single Blob, sorted by batchIndex. */
export async function getRecordingBlob(recordingId: number): Promise<Blob | null> {
  try {
    const promise = getDB()
    if (!promise) return null
    const db = await promise
    const chunks = await db.getAllFromIndex('chunks', 'recordingId', recordingId)
    if (chunks.length === 0) return null
    chunks.sort((a, b) => a.batchIndex - b.batchIndex)
    return new Blob(chunks.map((c) => c.data), { type: 'audio/webm' })
  } catch (e) {
    console.warn('[call-recording-db] getRecordingBlob failed:', e)
    return null
  }
}

/** Delete a recording and all its chunks. */
export async function deleteRecording(recordingId: number): Promise<void> {
  try {
    const promise = getDB()
    if (!promise) return
    const db = await promise
    // Delete chunks first
    const chunks = await db.getAllFromIndex('chunks', 'recordingId', recordingId)
    const tx = db.transaction('chunks', 'readwrite')
    for (const chunk of chunks) {
      if (chunk.id != null) tx.store.delete(chunk.id)
    }
    await tx.done
    // Delete the recording
    await db.delete('recordings', recordingId)
  } catch (e) {
    console.warn('[call-recording-db] deleteRecording failed:', e)
  }
}

/** Find orphaned recordings and retry uploading them. Toasts the result. */
export async function retryOrphanedRecordings(apiClient: typeof api): Promise<void> {
  const orphans = await getOrphanedRecordings()
  if (orphans.length === 0) return

  console.log(`[call-recording-db] Found ${orphans.length} orphaned recording(s), retrying upload...`)
  let succeeded = 0
  let failed = 0

  for (const recording of orphans) {
    if (recording.id == null) continue
    try {
      await updateRecordingStatus(recording.id, 'uploading')
      const blob = await getRecordingBlob(recording.id)
      if (!blob || blob.size < 1000) {
        // Too small to be useful — discard
        await deleteRecording(recording.id)
        continue
      }

      const form = new FormData()
      form.append('audio', blob, 'call-recording.webm')
      form.append('treatments', '')
      form.append('clinical_type', 'auto')
      form.append('source', 'web_call')
      if (recording.callNoteId) form.append('existing_note_id', String(recording.callNoteId))
      if (recording.patientUuid) form.append('patient_uuid', recording.patientUuid)
      if (recording.appointmentId) form.append('appointment_id', String(recording.appointmentId))
      if (recording.callNotes?.trim()) {
        form.append('had_text', '1')
        form.append('doctor_text', recording.callNotes)
      }

      await apiClient.postForm('/notes/generate?async=1', form, {
        // Reuse the recording's persisted key so this retry dedupes against an
        // upload that may have already committed before the crash. Records
        // written before idempotencyKey shipped fall back to a fresh key.
        headers: { 'Idempotency-Key': recording.idempotencyKey ?? crypto.randomUUID() },
      })
      await deleteRecording(recording.id)
      succeeded++
    } catch (e) {
      console.warn('[call-recording-db] Failed to retry orphaned recording:', e)
      // Revert to pending so it can be retried next time
      if (recording.id != null) {
        await updateRecordingStatus(recording.id, 'pending_upload')
      }
      failed++
    }
  }

  if (succeeded > 0) {
    toast.success(
      `Recovered ${succeeded} call recording${succeeded > 1 ? 's' : ''}`,
      'Previously interrupted uploads have been submitted for processing.',
    )
  }
  if (failed > 0) {
    toast.warning(
      `${failed} recording${failed > 1 ? 's' : ''} could not be uploaded`,
      'They will be retried automatically on next login.',
    )
  }
}
